import type { ReactNode } from 'react'
import type { DraftState, WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IssueList } from '../../../Services/OrderWizard/issues.ts'
import { selectFlaggedGroups } from '../../../Flux/Selector/OrderWizard/OrderWizardSelectors.ts'
import { isArchiveOrder } from '../../../Services/OrderWizard/archive.ts'
import { DiscoverySummaryView, OrderSettingsSection } from './DiscoverySummaryView.tsx'
import { WizardFileList } from './WizardFileList.tsx'
import { PoolsStep } from './PoolsStep.tsx'
import { ArchivePanel } from './ArchivePanel.tsx'
import { SamplePrimersGrid } from './SamplePrimersGrid.tsx'
import { SheetPanel } from './SheetPanel.tsx'
import { ReviewAndSubmit } from './ReviewAndSubmit.tsx'
import { DroppedSheetSupport } from './SendSheetCard.tsx'
import { R1HalvesNotice } from './ReverseFiles.tsx'

export const StepBody = ({ draft, orderId, step, issues, onShowIssues, dropzone, pasteRequests }: StepBodyProps) => {
  const flagged = selectFlaggedGroups(issues, step)
  switch (step) {
    case 'files': {
      const readd = Object.values(draft.files).some((file) => file.upload?.status === 'needs-readd')
      return (
        <>
          {readd && dropzone}
          {draft.discovery ? (
            <DiscoverySummaryView discovery={draft.discovery} settings={draft.settings} edit={draft} draft={draft} />
          ) : (
            /* a draft resumed from its order has read no files */
            draft.settings && <OrderSettingsSection settings={draft.settings} platform={null} edit={draft} draft={draft} />
          )}
          <R1HalvesNotice draft={draft} />
          <WizardFileList draft={draft} />
          <DroppedSheetSupport draft={draft} />
          {!readd && dropzone}
        </>
      )
    }
    case 'pools':
      return isArchiveOrder(draft) ? <ArchivePanel draft={draft} /> : <PoolsStep draft={draft} flagged={flagged} />
    case 'primers':
      return isArchiveOrder(draft) ? (
        <p className="text-sm text-slate-600">
          The exact primers of an archive&apos;s samples come from the sheet; see Samples &amp; controls.
        </p>
      ) : (
        <SamplePrimersGrid draft={draft} flagged={flagged} pasteRequests={pasteRequests} />
      )
    case 'metadata':
      return (
        <>
          <DroppedSheetSupport draft={draft} />
          <SheetPanel draft={draft} />
        </>
      )
    case 'review':
      return <ReviewAndSubmit draft={draft} orderId={orderId} issues={issues} onShowIssues={onShowIssues} />
  }
}

type StepBodyProps = {
  draft: DraftState
  orderId: string
  step: WizardStep
  issues: IssueList
  onShowIssues: () => void
  /** Shown on Files only; elsewhere the page overlay and the header's "Add files" take drops. */
  dropzone: ReactNode
  /** Bumped by a card asking the primers grid to open its paste box. */
  pasteRequests?: number
}
